import MD5 from 'md5'
import type { AxiosRequestConfig } from 'axios'
import { getSysTime } from '@/api/user'
import { authHttp, http, mkHttp } from '@/utils/request'

/** 可选的域名实例，与 request.ts 中的三个实例一一对应 */
export type SignService = 'api' | 'mkapi' | 'auth'

export interface SignRequestOptions {
  /** 接口路径（不含域名前缀，前缀由所选实例的 baseURL 提供） */
  url: string
  /** 请求方法，默认 POST */
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  /** 请求体，POST / PUT 使用，不会被拼到 url 上 */
  data?: any
  /** 额外的业务查询参数（可选），与签名参数一起拼在 url 上，不参与签名计算 */
  params?: Record<string, any>
  /** 短信登录场景，签名参数附加 auth_type=sms */
  sms?: boolean
  /** 使用哪个域名实例，默认 api */
  service?: SignService
  /** 透传给 axios 的配置，如 showLoading / showError / returnRaw / headers */
  config?: AxiosRequestConfig
  auth_type?: string
  action_type?: string
}

const NONCE_LENGTH = 32

const CHARS = 'ABCDEFGHJKMNPQRSTWXYZabcdefhijkmnprstwxyz2345678oOLl9gqVvUuI1'

const services = {
  api: http,
  mkapi: mkHttp,
  auth: authHttp,
} satisfies Record<SignService, typeof http>

/** 生成随机串，字符集与后端约定保持一致 */
const randomString = (len = NONCE_LENGTH) => {
  let result = ''
  for (let i = 0; i < len; i++) {
    result += CHARS.charAt(Math.floor(Math.random() * CHARS.length))
  }
  return result
}

/** 按 key 升序拼接 key + value 并统一转小写，作为待签名字符串 */
const objectSort = (obj: Record<string, unknown>) =>
  Object.keys(obj)
    .sort()
    .reduce((acc, key) => acc + key.toLowerCase() + String(obj[key]).toLowerCase(), '')

/** 兼容 getSysTime 返回时间戳本身或包装成对象两种结构 */
const pickTimestamp = (res: any): string | number => {
  if (typeof res === 'number' || typeof res === 'string') return res
  const timestamp =
    res?.currentTime ?? res?.timestamp ?? res?.sysTime ?? res?.systemTime ?? res?.time
  if (timestamp === undefined || timestamp === null) {
    throw new Error('获取服务器时间失败')
  }
  return timestamp
}

/**
 * 取服务器时间并生成签名参数
 * @param sms 短信登录场景附加 auth_type=sms
 */
export const buildSignParams = async (sms = false, auth_type="",action_type="") => {
  const timestamp = pickTimestamp(await getSysTime())

  const signParams: Record<string, string | number> = {}
  if (sms) signParams.auth_type = 'sms'
  if(auth_type) signParams["auth_type"] = auth_type
  if(action_type) signParams["action"] = action_type
  signParams.timestamp = timestamp
  signParams.nonce = randomString()
  signParams.signature = MD5(objectSort(signParams)).toUpperCase()

  return signParams
}

/**
 * 带签名的请求：先取服务器时间生成 timestamp / nonce / signature 拼在 url 上，
 * 请求体仍然走 data；再由 request.ts 的 axios 实例发出
 * （accessToken、loading、错误提示复用拦截器逻辑）
 *
 * @example
 * const res = await signRequest<LoginResult>({
 *   url: '/api/client/login',
 *   service: 'auth',
 *   data: params,
 *   config: { showLoading: true },
 * })
 */
export const signRequest = async <T = unknown>({
  url,
  method = 'POST',
  data,
  params,
  sms = false,
  service = 'api',
  config,
  auth_type = "",
  action_type = ""
}: SignRequestOptions): Promise<T> => {
  const query = { ...params, ...(await buildSignParams(sms,auth_type,action_type)) }
  const client = services[service]

  switch (method) {
    case 'GET':
      return client.get<T>(url, query, config)
    case 'DELETE':
      return client.delete<T>(url, query, config)
    case 'PUT':
      return client.put<T>(url, data, { ...config, params: query })
    default:
      return client.post<T>(url, data, { ...config, params: query })
  }
}

export default signRequest
